import Vue from 'vue';
import { Client, Settings } from '@/interfaces/multiTenancy';
import { Commit } from 'vuex';

interface ClientState {
	clients: {
		[id: number]: Client;
	};
	selectedClient: Client | null;
}

interface ClientEdit {
	css: string;
	description: string;
	name: string;
	settings: Settings;
	tzname: string;
	site?: string;
}

export default {
	namespaced: true,
	state: {
		clients: [],
		selectedClient: null,
	} as ClientState,
	getters: {
		getClients: (state: ClientState): Client[] => {
			return Object.values(state.clients);
		},

		getClient:
			(state: ClientState) =>
			(id: number): Client | undefined => {
				return state.clients[id];
			},

		getSelectedClient: (state: ClientState): Client | null => {
			return state.selectedClient;
		},
	},
	mutations: {
		SET_CLIENTS(state: ClientState, clients: Client[]): void {
			const ret: { [id: number]: Client } = {};
			state.clients = clients.reduce((obj, client) => {
				obj[client.id] = client;
				return obj;
			}, ret);
		},

		ADD_CLIENT(state: ClientState, client: Client): void {
			Vue.set(state.clients, client.id, client);
		},

		CHANGE_CLIENT(state: ClientState, client: Client & { id: number }): void {
			const oldClient = state.clients[client.id] || {};
			Vue.set(state.clients, client.id, { ...oldClient, ...client });
		},

		CHANGE_SELECTED_CLIENT(state: ClientState, client: Client): void {
			state.selectedClient = client;
		},

		CLEAN_CLIENT(state: ClientState): void {
			state.clients = {};
		},

		CLEAN_SELECTED_CLIENT(state: ClientState): void {
			state.selectedClient = null;
		},
	},
	actions: {
		async fetchClients({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/clients`, {
				headers: {
					Authorization: Vue.$cookies.get('Global-Token').token,
				},
			});

			if (response && response.status === 200) {
				commit('SET_CLIENTS', response.data.data);
			}
		},

		async addClient(
			{ commit }: { commit: Commit },
			{ client }: { client: ClientEdit }
		): Promise<void> {
			const response = await Vue.axios.post(`${process.env.VUE_APP_API_URL}/clients`, client);
			if (response && response.status === 200) {
				commit('ADD_CLIENT', response.data.data);
			}
		},

		async modifyClient(
			{ commit }: { commit: Commit },
			{ id, client }: { id: number; client: ClientEdit }
		): Promise<void> {
			const response = await Vue.axios.put(`${process.env.VUE_APP_API_URL}/clients/${id}`, client);
			if (response && response.status === 200) {
				commit('CHANGE_CLIENT', response.data.data);
			}
		},

		selectClient({ commit }: { commit: Commit }, { client }: { client: Client }): void {
			if (client) {
				commit('CHANGE_SELECTED_CLIENT', client);
			}
		},

		cleanClient({ commit }: { commit: Commit }): void {
			commit('CLEAN_CLIENT');
		},

		cleanSelectedClient({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_CLIENT');
		},

		cleanAllClient({ commit }: { commit: Commit }): void {
			commit('CLEAN_CLIENT');
			commit('CLEAN_SELECTED_CLIENT');
		},
	},
};
